/** Plugin detail page — dynamic form rendered from plugin parameter metadata (FR-SPA-04). */

import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  Autocomplete,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import { usePlugin } from "@/hooks/usePlugins";
import { useSubmitJob } from "@/hooks/useJobs";
import CompanySearch from "@/components/CompanySearch";
import EntitySearch from "@/components/EntitySearch";
import ModelSelector from "@/components/ModelSelector";
import AssetTypeSelect from "@/components/AssetTypeSelect";
import PromptInput from "@/components/PromptInput";
import GDrivePicker from "@/components/GDrivePicker";
import { useGDriveCredential } from "@/hooks/useGDriveCredential";
import type { ParameterDef, GDrivePickerValue } from "@/types";

export default function PluginDetail() {
  const { pluginId } = useParams<{ pluginId: string }>();
  const { data: plugin, isLoading } = usePlugin(pluginId ?? "");
  const submitJob = useSubmitJob();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dateFormat, setDateFormat] = useState<Record<string, string>>({});
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [jobName, setJobName] = useState("");

  // Initialise form with parameter defaults on first load
  useEffect(() => {
    if (plugin?.parameters && !defaultsApplied) {
      const defaults: Record<string, unknown> = {};
      for (const p of plugin.parameters) {
        if (p.default !== undefined && p.default !== null) {
          defaults[p.name] = p.default;
        }
      }
      if (Object.keys(defaults).length > 0) {
        setValues(defaults);
      }
      setDefaultsApplied(true);
    }
  }, [plugin, defaultsApplied]);

  const set = (name: string, value: unknown) => setValues((prev) => ({ ...prev, [name]: value }));

  // Group parameters
  const groups = useMemo(() => {
    if (!plugin?.parameters) return {};
    const g: Record<string, ParameterDef[]> = {};
    for (const p of plugin.parameters) {
      const group = p.group || "General";
      (g[group] ??= []).push(p);
    }
    return g;
  }, [plugin]);

  const handleSubmit = () => {
    if (!plugin) return;
    submitJob.mutate({ pluginId: plugin.pluginId, parameters: values, name: jobName || undefined });
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  if (!plugin) return <Alert severity="error">Plugin not found</Alert>;

  const hasGDriveParams = plugin.parameters.some((p) => p.type === "gdrive_file_picker" || p.type === "gdrive_folder_picker");

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{plugin.name}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{plugin.description}</Typography>

      {hasGDriveParams && <ScopeUpgradeBanner />}

      {submitJob.isError && <Alert severity="error" sx={{ mb: 2 }}>{submitJob.error.message}</Alert>}

      <TextField
        label="Job Name"
        value={jobName}
        onChange={(e) => setJobName(e.target.value)}
        helperText="Optional name to identify this job"
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        inputProps={{ maxLength: 200 }}
      />

      {Object.entries(groups).map(([group, params]) => (
        <Accordion key={group} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{group}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {params.map((p) => (
                <FieldRenderer key={p.name} param={p} value={values[p.name]} onChange={(v) => set(p.name, v)} dateFormat={dateFormat[p.name] ?? "range"} onDateFormatChange={(f) => setDateFormat((prev) => ({ ...prev, [p.name]: f }))} pluginId={plugin.pluginId} />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmit} disabled={submitJob.isPending} sx={{ mt: 3 }} size="large">
        {submitJob.isPending ? "Submitting…" : "Run Plugin"}
      </Button>
    </Box>
  );
}

function FieldRenderer({ param, value, onChange, dateFormat, onDateFormatChange, pluginId }: {
  param: ParameterDef; value: unknown; onChange: (v: unknown) => void;
  dateFormat: string; onDateFormatChange: (f: string) => void; pluginId?: string;
}) {
  const { type, label, help_text, choices, required } = param;

  switch (type) {
    case "text":
      return <TextField label={label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} helperText={help_text} required={required} size="small" fullWidth />;

    case "integer":
      return <TextField label={label} type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} helperText={help_text} required={required} size="small" fullWidth />;

    case "boolean":
      return (
        <Box>
          <FormControlLabel control={<Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />} label={label} />
          {help_text && <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: "34px", mt: -0.5 }}>{help_text}</Typography>}
        </Box>
      );

    case "select":
      return (
        <TextField label={label} select value={value ?? ""} onChange={(e) => onChange(e.target.value)} helperText={help_text} required={required} size="small" fullWidth>
          {(choices ?? []).map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
      );

    case "multi_select":
      return (
        <Autocomplete multiple options={choices ?? []} getOptionLabel={(o) => o.label} value={(choices ?? []).filter((c) => (value as string[] ?? []).includes(c.value))} onChange={(_, v) => onChange(v.map((o) => o.value))} renderInput={(p) => <TextField {...p} label={label} helperText={help_text} size="small" />} renderTags={(v, getTagProps) => v.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.value} label={o.label} size="small" />)} />
      );

    case "date_range":
      return (
        <Box>
          <ToggleButtonGroup value={dateFormat} exclusive onChange={(_, v) => { if (v) { onDateFormatChange(v); onChange(undefined); } }} size="small" sx={{ mb: 1 }}>
            <ToggleButton value="range">Date Range</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
          {dateFormat === "range" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField label="Start" type="date" value={(value as Record<string, string>)?._from_date ?? ""} onChange={(e) => {
                const v = e.target.value;
                const prev = (value as Record<string, unknown>) ?? {};
                const updates = v ? { from: Math.floor(new Date(v + "T00:00:00").getTime() / 1000), start: v, _from_date: v } : { from: undefined, start: "", _from_date: "" };
                onChange({ ...prev, ...updates });
              }} size="small" slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="End" type="date" value={(value as Record<string, string>)?._to_date ?? ""} onChange={(e) => {
                const v = e.target.value;
                const prev = (value as Record<string, unknown>) ?? {};
                const updates = v ? { to: Math.floor(new Date(v + "T23:59:59").getTime() / 1000), end: v, _to_date: v } : { to: undefined, end: "", _to_date: "" };
                onChange({ ...prev, ...updates });
              }} size="small" slotProps={{ inputLabel: { shrink: true } }} />
            </Box>
          )}
          {dateFormat === "month" && (
            <TextField label="Month" type="month" value={(value as Record<string, string>)?._month ?? ""} onChange={(e) => {
              const m = e.target.value; // "YYYY-MM"
              if (!m) { onChange(undefined); return; }
              const [y, mo] = m.split("-").map(Number);
              if (!y || !mo) { onChange(undefined); return; }
              const startDate = `${y}-${String(mo).padStart(2, "0")}-01`;
              const endDay = new Date(y, mo, 0); // last day of month
              const endDate = `${y}-${String(mo).padStart(2, "0")}-${String(endDay.getDate()).padStart(2, "0")}`;
              const fromTs = Math.floor(new Date(startDate + "T00:00:00").getTime() / 1000);
              const toTs = Math.floor(new Date(endDate + "T23:59:59").getTime() / 1000);
              onChange({ from: fromTs, to: toTs, start: startDate, end: endDate, _from_date: startDate, _to_date: endDate, _month: m });
            }} size="small" slotProps={{ inputLabel: { shrink: true } }} />
          )}
          {dateFormat === "week" && (
            <TextField label="Week" type="week" value={(value as Record<string, string>)?._week ?? ""} onChange={(e) => {
              const w = e.target.value; // "YYYY-WNN"
              if (!w) { onChange(undefined); return; }
              const [yearStr, weekStr] = w.split("-W");
              const year = Number(yearStr);
              const week = Number(weekStr);
              if (!year || !week || isNaN(year) || isNaN(week)) { onChange(undefined); return; }
              // ISO week: Monday of week 1 is the week containing Jan 4
              // Use setDate() for DST-safe calendar arithmetic
              const jan4 = new Date(year, 0, 4);
              const dayOfWeek = jan4.getDay() || 7; // 1=Mon..7=Sun
              const mon = new Date(year, 0, 4 - (dayOfWeek - 1) + (week - 1) * 7);
              const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
              const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              const fromTs = Math.floor(mon.getTime() / 1000);
              const toTs = Math.floor((sun.getTime() + 86399000) / 1000); // end of day
              onChange({ from: fromTs, to: toTs, start: fmt(mon), end: fmt(sun), _from_date: fmt(mon), _to_date: fmt(sun), _week: w });
            }} size="small" slotProps={{ inputLabel: { shrink: true } }} />
          )}
          {help_text && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{help_text}</Typography>}
        </Box>
      );

    case "prompt":
      return <PromptInput value={(value as string) ?? ""} onChange={(v) => onChange(v)} label={label} helperText={help_text} />;

    case "model_select":
      return <ModelSelector value={(value as string) ?? ""} onChange={onChange} choices={choices ?? []} label={label} helperText={help_text} />;

    case "asset_type_select":
      return <AssetTypeSelect value={(value as string) ?? ""} onChange={onChange} choices={choices ?? []} label={label} helperText={help_text} />;

    case "company_search":
      return <CompanySearch label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "project_search":
      return <EntitySearch entityType="accelo_project" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "retainer_search":
      return <EntitySearch entityType="accelo_retainer" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "contact_search":
      return <EntitySearch entityType="accelo_contact" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "staff_search":
      return <EntitySearch entityType="accelo_staff" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "opportunity_search":
      return <EntitySearch entityType="accelo_opportunity" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "zendesk_org_search":
      return <EntitySearch entityType="zendesk_organization" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "zendesk_user_search":
      return <EntitySearch entityType="zendesk_user" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "datadog_monitor_search":
      return <EntitySearch entityType="datadog_monitor" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "datadog_slo_search":
      return <EntitySearch entityType="datadog_slo" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "datadog_host_search":
      return <EntitySearch entityType="datadog_host" label={label} value={value as never} onChange={onChange} multiple={param.multiple} helperText={help_text} pluginId={pluginId} credential={param.credential} />;

    case "gdrive_file_picker":
      return <GDrivePicker mode="file" label={label} value={(value as GDrivePickerValue) ?? null} onChange={onChange} helperText={help_text} showRange={param.show_range} mimeFilter={param.mime_filter} />;

    case "gdrive_folder_picker":
      return <GDrivePicker mode="folder" label={label} value={(value as GDrivePickerValue) ?? null} onChange={onChange} helperText={help_text} showTemplatePicker={param.show_template_picker} templateType={param.template_type} />;

    default:
      return <TextField label={label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} helperText={help_text} size="small" fullWidth />;
  }
}

function ScopeUpgradeBanner() {
  const { scopeStatus, reconnect } = useGDriveCredential();
  if (scopeStatus !== "needs_upgrade") return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }} action={
      <Button color="inherit" size="small" onClick={reconnect}>Reconnect Google Drive</Button>
    }>
      This plugin requires expanded Google Drive access.
    </Alert>
  );
}
